import axios from "axios";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import config from "../config";
import i18n from "i18next";

export const BackendApi = axios.create({
  baseURL: config.backendUrl,
  withCredentials: false,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Cache-Control": "no-store",
  },
});

BackendApi.interceptors.response.use(
  (res) => {
    return res;
  },
  (err) => {
    if (
      err.response?.data?.message === "Not authorized" ||
      err.response?.status === 401
    ) {
      Cookies.remove("token");
    }

    // Отправляем критичные HTTP ошибки в Sentry
    if (err.response) {
      const status = err.response.status;
      const url = err.config?.url;
      const method = err.config?.method?.toUpperCase();

      // Отправляем только серверные ошибки (5xx) и некоторые клиентские
      const shouldReport =
        status >= 500 || // Серверные ошибки
        status === 429 || // Too Many Requests
        status === 408; // Request Timeout

      if (shouldReport) {
        Sentry.captureException(err, {
          tags: {
            error_type: "http_error",
            http_status: status,
            http_method: method,
          },
          contexts: {
            request: {
              url: url,
              method: method,
              status: status,
              data: err.config?.data,
            },
            response: {
              data: err.response.data,
              status: err.response.status,
              statusText: err.response.statusText,
            },
          },
          level: status >= 500 ? "error" : "warning",
        });
      }
    } else if (err.request) {
      // Запрос был отправлен, но ответа не пришло (сетевая ошибка)
      // Отправляем в Sentry только если это не единичный сбой
      const errorMessage = err.message || "Network request failed";

      // Не спамим Sentry временными сетевыми сбоями
      // Логируем только если это критично
      if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") {
        console.warn("⚠️ Network error:", errorMessage);

        // Можно добавить счетчик и отправлять в Sentry только после N неудач
        // Пока просто логируем
      }
    }

    throw err;
  }
);

BackendApi.interceptors.request.use(
  (req) => {
    const token = Cookies.get("token");
    req.headers["Accept-Language"] = i18n.language;
    if (!token) {
      if (req.url.slice(0, 5) === "games") {
        delete req.headers.Authorization;
      }
      return req;
    }
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  },
  () => {}
);

export const API_ROUTES = {
  SAVE_WEB_FCM: "users/web-device-token",
  AUTH: "users/auth",
  USERS: "users",
  GAME_EVENTS: "game-events",
  REGISTER: "users/registration",
  REGISTER_CODE: "users/registration/code",
  LOGIN: "users/login",
  SEND_EMAIL: "email_code/send_email",
  SEND_CODE: "users/check_code",
  CURRENT_USER: "users/current",
  DELETE_USER: "users/remove",
  REFERRAL_HANDLE: "events/link",
  PASSWORD_UPDATE: "users/change_pass",
  USER_DATA_UPDATE: "users/edit",
  ADD_BUSINESS_PARTNER_FOR_USER: "users/business-partner-for-user",
  ORDER_ID: "purchase/orders",
  PAYPLUS_ORDER_ID: "purchase/orders/payplus",
  PURCHASE_PRICES: "purchase/prices",
  CHECK_PURCHASED: "purchase/check-purchased",
  POINTS_BALANCE: "points/balance",
  POINTS_HISTORY: "points/history",
  POINTS_REF_CODE: "points/ref-code",
  POINTS_REFERRAL_STATS: "points/referral-stats",
  POINTS_SPEND: "points/spend",
  JOIN_GAME: "games",
  INVITE_LINK: "games/purchase-game/",
  QUESTIONS_CARDS: "questions",
  ANSWERS: "answers",
  ANSWERS_EDIT: "answers/edit",
  ANSWERS_CHECK: "answers/check/",
  MATCHES: "games/coincidences/",
  MATCHES_ALL: "games/coincidences/all-games",
  REMOVE_GAME: "games/delete-game",
  CHOOSE_FORMAT: "selected-subgames",
  CHECK_FORMAT: "selected-subgames/check/",
  COMPROMISE_ANSWERS: "compromise",
  GET_COMPROMISE_QUESTIONS: "compromise/questions/",
  GET_COMPROMISE_MATCHES_CHECK: "compromise/check/",
  GET_COMPROMISE_MATCHES: "compromise/",
  GET_HISTORY_COMPROMISE_GAME: "compromise/game-history/",
  GAMES: "games",
  ZONES: "zones/",
  CREATE_INSIGHT_FORM: "insight-form",
  GET_INSIGHT_FORMS: "insight-form/game/",
  GET_INSIGHT_FORMS_QUESTION: "insight-form/question/",
  GET_INSIGHT_FORMS_PAIR: "insight-form/pair/",
  SEND_INSIGHT_FORM_ANSWER: "insight-form/",
  SEND_INSIGHT_FORM_DRAFT: "insight-form/",
  GET_INSIGHT_FORM_ANSWERS: "insight-form/",
  GET_INSIGHT_FORM_DRAFT: "insight-form/",

  START_MARATHON_NEXT_STEP: "marathons/next-step",
  SELECT_MARATHON_CARD: "marathons/answers-marathon",
  CHECK_MARATHON_PICK: "marathons/check-answers/?",
  SEND_FEEDBACK: "reviews",
  GET_HISTORY_GAME: "answers/game-history/",
  GET_USER_ANSWER: "answers/check-user/",
  COPY_GAME_ANSWER: "answers/copy-last-game/",
  GET_MARATHON_STEPS: "marathons/marathon-steps/",
  SEND_MARATHON_STATE_STEP: "marathons/marathon-steps",
  SELECT_PRE_PICK_PREPARE_CARD: "marathons/pre-election-answers-marathon",
  FINISH_PRE_PICK_PREPARE: "marathons/check-pre-election-answers/?",
  CHECK_SELECTED_PREPARE_PRE_PICK_CARDS:
    "/marathons/pre-election-answers-marathon?",

  RESET_GAME_PROGRESS: "answers/reset",
  RESET_GAME_ANSWERS_PROGRESS: "answers/reset/answers",
  SEND_SUPPORT_FORM: "email/support",
  SEND_SHOP_NOTIFY: "email/shop-notify",
  GET_GAME_REVIEWS: "reviews",

  TEMPORARY_CURRENT_USER: "games/by-temporary/",
};

const mapRequests = {
  save_fcm_web: async (data) =>
    postRequestWrapper(API_ROUTES.SAVE_WEB_FCM, data),
  temporary_current_user: async (data) =>
    await getRequestWrapper(
      API_ROUTES.TEMPORARY_CURRENT_USER + data.temporaryUserId
    ),
  createDemoGame: async (data) =>
    await postRequestWrapper(API_ROUTES.GAMES, data),
  getZones: async (data) =>
    await getRequestWrapper(API_ROUTES.ZONES + data.gameId),
  //INSIGHT_FORM
  createInsightForm: async (data) =>
    await postRequestWrapper(API_ROUTES.CREATE_INSIGHT_FORM, data),
  getInsightForms: async (data) =>
    await getRequestWrapper(API_ROUTES.GET_INSIGHT_FORMS + data.payload),
  getInsightFormDraftCurrent: async (data) =>
    await getRequestWrapper(
      API_ROUTES.GET_INSIGHT_FORM_DRAFT + "draft/" + data.payload
    ),
  getInsightFormDrafts: async () =>
    await getRequestWrapper(API_ROUTES.GET_INSIGHT_FORM_DRAFT + "draft"),
  sendInsightFormDraft: async (data) =>
    await postRequestWrapper(
      API_ROUTES.SEND_INSIGHT_FORM_DRAFT + "draft/" + data.formId,
      data
    ),
  getInsightFormPairByQuestionId: async (data) =>
    await getRequestWrapper(
      API_ROUTES.GET_INSIGHT_FORMS_QUESTION +
        data.payload.questionId +
        `?gameId=${data.payload.gameId}`
    ),
  // пара BLANK-анкет (без questionId) — по pairKey
  getInsightFormPairByPairKey: async (data) =>
    await getRequestWrapper(
      API_ROUTES.GET_INSIGHT_FORMS_PAIR +
        data.payload.pairKey +
        `?gameId=${data.payload.gameId}`
    ),
  sendInsightFormAnswer: async (data) =>
    await postRequestWrapper(
      API_ROUTES.SEND_INSIGHT_FORM_ANSWER + data.formId,
      data.payload
    ),
  getInsightFormAnswer: async (data) =>
    await getRequestWrapper(API_ROUTES.GET_INSIGHT_FORM_ANSWERS + data.formId),

  // GAME
  resetGameProgress: async (data) =>
    await postRequestWrapper(API_ROUTES.RESET_GAME_PROGRESS, data),
  resetGameAnswersProgress: async (data) =>
    await postRequestWrapper(API_ROUTES.RESET_GAME_ANSWERS_PROGRESS, data),
  addBusinessPartnerForUser: async (data) =>
    await postRequestWrapper(API_ROUTES.ADD_BUSINESS_PARTNER_FOR_USER, data),
  feedback: async (data) =>
    await postRequestWrapper(API_ROUTES.SEND_FEEDBACK, data),
  getGameReviews: async (data) =>
    await getRequestWrapper(API_ROUTES.GET_GAME_REVIEWS + data.payload),
  // USUAL_GAME
  questions_cards: async (data) =>
    getRequestWrapper(
      data?.setType
        ? `${API_ROUTES.QUESTIONS_CARDS}?setType=${data.setType}`
        : API_ROUTES.QUESTIONS_CARDS
    ),
  answers: async (data) => await postRequestWrapper(API_ROUTES.ANSWERS, data),
  answers_edit: async (data) =>
    await postRequestWrapper(API_ROUTES.ANSWERS_EDIT, data),
  answersCheck: async (data) =>
    await getRequestWrapper(API_ROUTES.ANSWERS_CHECK + data.gameId, data),
  getHistoryGame: async (data) =>
    await getRequestWrapper(
      API_ROUTES.GET_HISTORY_GAME +
        data.gameId +
        `?${
          data.temporaryUserId
            ? `temporaryUserId=${data.temporaryUserId}`
            : `userId=${data.userId}`
        }`,
      data
    ),
  getUserAnswers: async (data) =>
    await getRequestWrapper(
      API_ROUTES.GET_USER_ANSWER +
        `?${
          data.temporaryUserId
            ? `temporaryUserId=${data.temporaryUserId}`
            : `userId=${data.userId}`
        }`,
      data
    ),
  copyGameAnswer: async (data) =>
    await postRequestWrapper(API_ROUTES.COPY_GAME_ANSWER, data),
  sendSupportForm: async (data) =>
    await postRequestWrapper(API_ROUTES.SEND_SUPPORT_FORM, data),
  // item 2: заявка «уведомить, когда магазин заработает в моей стране»
  sendShopNotify: async (data) =>
    await postRequestWrapper(API_ROUTES.SEND_SHOP_NOTIFY, data),
  // USUAL_GAME

  // GAME_MARATHON
  startMarathonNextStep: async (data) =>
    postRequestWrapper(API_ROUTES.START_MARATHON_NEXT_STEP, data),
  checkMarathonPicks: async (data) =>
    getRequestWrapper(API_ROUTES.CHECK_MARATHON_PICK + data.payload),
  selectMarathonCard: async (data) =>
    postRequestWrapper(API_ROUTES.SELECT_MARATHON_CARD, data),
  getMarathonStepMap: async (data) =>
    await getRequestWrapper(API_ROUTES.GET_MARATHON_STEPS + data.gameId, data),
  sendStateStep: async (data) =>
    await postRequestWrapper(API_ROUTES.SEND_MARATHON_STATE_STEP, data),
  selectPrePickPrepareCard: async (data) =>
    await postRequestWrapper(API_ROUTES.SELECT_PRE_PICK_PREPARE_CARD, data),
  finishPrePickPrepare: async (data) =>
    await getRequestPayloadWrapper(
      API_ROUTES.FINISH_PRE_PICK_PREPARE + data.payload,
      data
    ),
  checkSelectedPrePickPrepareCards: async (data) =>
    await getRequestPayloadWrapper(
      API_ROUTES.CHECK_SELECTED_PREPARE_PRE_PICK_CARDS + data.payload,
      data
    ),
  // GAME_MARATHON

  //
  coincidences: async (data) =>
    await getRequestPayloadWrapper(API_ROUTES.MATCHES + data.payload),
  all_games_coincidences: async () =>
    await getRequestWrapper(API_ROUTES.MATCHES_ALL),
  //

  // GAME_COMPROMISE
  get_compromise_matches_check: async (data) =>
    getRequestWrapper(API_ROUTES.GET_COMPROMISE_MATCHES_CHECK + data.gameId),
  get_compromise_matches: async (data) =>
    getRequestWrapper(API_ROUTES.GET_COMPROMISE_MATCHES + data.gameId),
  getHistoryCompromiseGame: async (data) =>
    getRequestWrapper(API_ROUTES.GET_HISTORY_COMPROMISE_GAME + data.gameId),
  compromise_answers: async (data) =>
    await postRequestWrapper(API_ROUTES.COMPROMISE_ANSWERS, data),
  getCompromiseQuestions: async (data) =>
    await getRequestWrapper(API_ROUTES.GET_COMPROMISE_QUESTIONS + data.gameId),
  // GAME_COMPROMISE

  // CHOOSE_FORMAT
  check_choose_format: async (data) =>
    getRequestWrapper(API_ROUTES.CHECK_FORMAT + data.gameId),
  choose_format: async (data) =>
    postRequestWrapper(API_ROUTES.CHOOSE_FORMAT, data),
  // CHOOSE_FORMAT

  // GAME

  // AUTH
  authorization: async (data) => await getRequestWrapper(API_ROUTES.AUTH, data),
  login: async (data) => await postRequestWrapper(API_ROUTES.LOGIN, data),
  register: async ({ formData, ref }) =>
    await postRequestWrapper(API_ROUTES.REGISTER + `/?ref=${ref}`, formData),
  registerCode: async (data) =>
    await postRequestWrapper(API_ROUTES.REGISTER_CODE, data),
  email: async (data) => await postRequestWrapper(API_ROUTES.SEND_EMAIL, data),
  code: async (data) => await postRequestWrapper(API_ROUTES.SEND_CODE, data),
  // AUTH

  // REFERRAL
  referral_handle: async (data) =>
    await postRequestWrapper(API_ROUTES.REFERRAL_HANDLE, data),
  // REFERRAL

  // USER
  current_user: async () => await getRequestWrapper(API_ROUTES.CURRENT_USER),
  delete_user: async () => await postRequestWrapper(API_ROUTES.DELETE_USER),
  user_data_update: async (data) =>
    await postRequestWrapper(API_ROUTES.USER_DATA_UPDATE, data),
  demonstration_finish: async (data) =>
    await patchRequestWrapper(
      `${API_ROUTES.USERS}/demonstration/${data.id}`,
      data
    ),
  password_update: async (data) => {
    // recovery-флоу кладёт JWT в pass_update_token (RecoveryCodeForm);
    // cookie "token" — фолбэк для залогиненного пользователя
    const passToken = Cookies.get("pass_update_token") || Cookies.get("token");
    return await axios.post(`${config.backendUrl}users/change_pass`, data, {
      headers: {
        Authorization: `Bearer ${passToken}`,
      },
    });
  },
  change_rules: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/rules/${data.id}`,
      data
    );
  },
  add_game_ids_by_greetings: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/greeting/${data.id}`,
      data
    );
  },
  add_game_ids_by_rules: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/rule/${data.id}`,
      data
    );
  },
  add_game_ids_pwa_instruction_popup: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/pwa-instruction-popup/${data.id}`,
      data
    );
  },
  add_game_ids_using_prev_answers_popup: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/prev-answers-popup/${data.id}`,
      data
    );
  },
  add_game_ids_by_meets: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.USERS}/meets/${data.id}`,
      data
    );
  },
  get_game_events: async (data) => {
    const gameEvents = await getRequestWrapper(`${API_ROUTES.GAME_EVENTS}`);
    return gameEvents;
  },
  game_notification: async (data) => {
    return await patchRequestWrapper(
      `${API_ROUTES.GAME_EVENTS}/game-notification/${data.id}`,
      data
    );
  },
  game_notification_by_gameid: async (data) => {
    return await putRequestWrapper(
      `${API_ROUTES.GAME_EVENTS}/game-notification/by-game-id`,
      data
    );
  },
  remove_game: async (data) =>
    await postRequestWrapper(API_ROUTES.REMOVE_GAME, data),
  // USER

  order_id: async (data) => await postRequestWrapper(API_ROUTES.ORDER_ID, data),
  payplus_order_id: async (data) =>
    await postRequestWrapper(API_ROUTES.PAYPLUS_ORDER_ID, data),
  join_game: async (data) =>
    await getRequestWrapper(
      `${API_ROUTES.JOIN_GAME}?link=${data.payload.link}&temporaryUserId=${data.payload.temporaryUserId}`
    ),
  invite_link: async (data) =>
    await getRequestPayloadWrapper(API_ROUTES.INVITE_LINK + data.payload.order),
  purchase_prices: async () =>
    await getRequestWrapper(API_ROUTES.PURCHASE_PRICES),
  check_purchased: async (data) =>
    await getRequestWrapper(
      `${API_ROUTES.CHECK_PURCHASED}?orderId=${data.payload.order}`
    ),
  points_balance: async () => await getRequestWrapper(API_ROUTES.POINTS_BALANCE),
  points_history: async () => await getRequestWrapper(API_ROUTES.POINTS_HISTORY),
  points_ref_code: async () =>
    await getRequestWrapper(API_ROUTES.POINTS_REF_CODE),
  points_referral_stats: async () =>
    await getRequestWrapper(API_ROUTES.POINTS_REFERRAL_STATS),
  points_spend: async (data) =>
    await postRequestWrapper(API_ROUTES.POINTS_SPEND, data),
};

async function requestManager(event, data, onSuccess, onError, onFinally) {
  try {
    const request = mapRequests[event];
    return await request(data)
      .then((response) => {
        if (response) {
          onSuccess && onSuccess(response);
          return response;
        }
      })
      .finally((data) => {
        onFinally && onFinally(data);
      })
      .catch((error) => {
        onError && onError(error);
      });
  } catch (error) {
    onError && onError(error);
  }
}

async function getRequestWrapper(url, data) {
  return await BackendApi.get(url);
}
async function getRequestPayloadWrapper(url) {
  return await BackendApi.get(url);
}

async function postRequestWrapper(url, data) {
  return await BackendApi.post(url, data);
}

async function patchRequestWrapper(url, data) {
  return await BackendApi.patch(url, data);
}

async function putRequestWrapper(url, data) {
  return await BackendApi.put(url, data);
}

export default {
  requestManager,
};
